import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Upload } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { api, formatApiError } from "@/lib/api";
import { img } from "@/lib/content";

const SECTORS = [
  "Banking & Financial Services",
  "Technology",
  "Real Estate & Construction",
  "Energy & Utilities",
  "Healthcare",
  "Legal",
  "Logistics & Supply Chain",
  "Consulting & Professional Services",
  "Other",
];

const UploadResume = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", location: "", current_title: "",
    experience_years: "", sector: "", target_locations: "", linkedin: "", notes: "", website: "", consent: false,
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("source_page", "upload-resume");
      if (resume) fd.append("resume", resume);
      const r = await api.post("/candidates", fd);
      setReference(r.data.reference);
      toast.success("Profile received.");
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Upload Your Resume — NEXREQ UAE & GCC"
        siteName="NEXREQ"
        description="Submit your profile to NEXREQ for confidential consideration across executive and professional mandates in the UAE and GCC."
      />
      <PageHero
        eyebrow="For Candidates"
        lines={["Put Your Profile", "in Front of the Right People."]}
        description="One submission. Considered across every relevant NEXREQ mandate in the region — confidentially and without obligation."
        image={img("photo-1573497019940-1c28c88b4f3e", 1920)}
      />

      <section className="bg-obsidian pb-28" data-testid="resume-section">
        <div className="nx-container max-w-3xl">
          {reference ? (
            <Reveal>
              <div className="border border-white/10 bg-surface p-10 lg:p-14" data-testid="resume-success">
                <span className="flex h-12 w-12 items-center justify-center bg-emerald-600/15 text-emerald-400">
                  <Check size={22} />
                </span>
                <h2 className="mt-7 font-heading text-3xl font-light text-white">Profile Received.</h2>
                <p className="mt-5 leading-relaxed text-white/55">
                  Thank you for contacting NEXREQ. Your candidate profile has been received. A member
                  of our UAE &amp; GCC team will review your profile and reach out when a mandate
                  matches your experience.
                </p>
                <p className="mt-7 inline-block border border-white/10 bg-obsidian px-5 py-3 font-mono text-xs tracking-[0.15em] text-emerald-400">
                  REF: {reference}
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to="/jobs" className="nx-btn-primary" data-testid="resume-success-jobs-btn">
                    Browse Open Roles <ArrowRight size={15} />
                  </Link>
                  <Link to="/" className="nx-btn-outline" data-testid="resume-success-home-btn">
                    Return Home
                  </Link>
                </div>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="border border-white/10 bg-surface p-8 lg:p-12" data-testid="resume-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="nx-label" htmlFor="cd-name">Full Name *</label>
                    <input id="cd-name" className="nx-input" required value={form.name} onChange={set("name")} data-testid="resume-name-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-email">Email *</label>
                    <input id="cd-email" type="email" className="nx-input" required value={form.email} onChange={set("email")} data-testid="resume-email-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-phone">Phone</label>
                    <input id="cd-phone" className="nx-input" value={form.phone} onChange={set("phone")} data-testid="resume-phone-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-location">Current Location</label>
                    <input id="cd-location" className="nx-input" value={form.location} onChange={set("location")} data-testid="resume-location-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-title">Current Job Title</label>
                    <input id="cd-title" className="nx-input" value={form.current_title} onChange={set("current_title")} data-testid="resume-title-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-exp">Years of Experience</label>
                    <input id="cd-exp" className="nx-input" placeholder="e.g. 12" value={form.experience_years} onChange={set("experience_years")} data-testid="resume-experience-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-sector">Sector</label>
                    <select id="cd-sector" className="nx-input cursor-pointer appearance-none [&>option]:bg-obsidian" value={form.sector} onChange={set("sector")} data-testid="resume-sector-select">
                      <option value="">Select a sector</option>
                      {SECTORS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="cd-target">Target Locations</label>
                    <input id="cd-target" className="nx-input" placeholder="e.g. Dubai, Riyadh, Doha" value={form.target_locations} onChange={set("target_locations")} data-testid="resume-target-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="nx-label" htmlFor="cd-linkedin">LinkedIn Profile</label>
                    <input id="cd-linkedin" className="nx-input" placeholder="https://linkedin.com/in/…" value={form.linkedin} onChange={set("linkedin")} data-testid="resume-linkedin-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="nx-label" htmlFor="cd-notes">Anything We Should Know</label>
                    <textarea id="cd-notes" rows={4} className="nx-input" value={form.notes} onChange={set("notes")} data-testid="resume-notes-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="nx-label">Resume (PDF / Word, max 8MB)</label>
                    <label
                      className="flex cursor-pointer items-center gap-3 border border-dashed border-white/15 bg-white/[0.02] px-5 py-6 text-sm text-white/50 transition-colors hover:border-emerald-500/50 hover:text-white/70"
                      data-testid="resume-file-dropzone"
                    >
                      <Upload size={18} className="shrink-0 text-emerald-500" />
                      {resume ? resume.name : "Attach your resume"}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => setResume(e.target.files?.[0] || null)}
                        data-testid="resume-file-input"
                      />
                    </label>
                  </div>
                </div>
                <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                <label className="mt-7 flex items-start gap-3 text-xs leading-relaxed text-white/50" data-testid="resume-consent-label">
                  <input type="checkbox" required checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" data-testid="resume-consent-checkbox" />
                  <span>I consent to NEXREQ processing my personal data for recruitment purposes, as described in the <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>.</span>
                </label>
                <button type="submit" className="nx-btn-primary mt-7 w-full sm:w-auto" disabled={submitting} data-testid="resume-submit-btn">
                  {submitting ? "Submitting…" : "Submit Profile"} <ArrowRight size={15} />
                </button>
                <p className="mt-5 text-xs leading-relaxed text-white/35">
                  Your details are held in confidence and shared only with your consent.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

export default UploadResume;
